// src/pages/ResetPassword.jsx
import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api/api";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      toast.error("This reset link is invalid or missing a token.");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      await api.post("/user/reset-password", { token, newPassword });
      setDone(true);
      toast.success("Password reset successfully!");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to reset password. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="bg-white/90 backdrop-blur-lg rounded-3xl shadow-2xl p-10 border border-purple-100 text-center">
            <div className="text-6xl mb-6">⚠️</div>
            <p className="text-xl font-semibold text-red-600">Invalid Link</p>
            <p className="text-gray-600 mt-4">
              This password reset link is missing or invalid. Please request a new one.
            </p>
            <Link to="/forgot-password" className="mt-8 inline-block text-purple-600 font-bold hover:underline">
              ← Request a new reset link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="bg-white/90 backdrop-blur-lg rounded-3xl shadow-2xl p-10 border border-purple-100">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-extrabold text-purple-700">Reset Password</h2>
            <p className="text-gray-600 mt-4">Enter your new password below</p>
          </div>

          {done ? (
            <div className="text-center py-10">
              <div className="text-6xl mb-6">✅</div>
              <p className="text-xl font-semibold text-green-600">Password reset!</p>
              <p className="text-gray-600 mt-4">You can now log in with your new password.</p>
              <Link to="/login" className="mt-8 inline-block text-purple-600 font-bold hover:underline">
                → Go to Login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-lg font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="w-full px-6 py-4 rounded-2xl border-2 border-gray-200 focus:border-purple-600 outline-none transition text-lg"
                  required
                />
              </div>

              <div>
                <label className="block text-lg font-medium text-gray-700 mb-2">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your new password"
                  className="w-full px-6 py-4 rounded-2xl border-2 border-gray-200 focus:border-purple-600 outline-none transition text-lg"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-xl font-bold rounded-2xl shadow-xl hover:opacity-90 transition disabled:opacity-70"
              >
                {loading ? "Resetting..." : "Reset Password"}
              </button>
            </form>
          )}

          <div className="text-center mt-8">
            <Link to="/login" className="text-purple-600 font-medium hover:underline">
              ← Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}